/**
 * What a leg is FOR, within its basket — ROADMAP 9.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CLOSED LIST, BECAUSE THE ANSWER IS SUMMED
 *
 * The point of labelling a leg is to be able to ask "what did the hedges
 * cost" and "did the adjustments help" — both of which are sums over a
 * category. Free text does not sum: "hedge", "Hedge" and "hedges" are three
 * categories, and the mistake is silent and permanent.
 *
 * So the vocabulary is fixed here, in one place, and the Prisma enum, the
 * request validator and the UI all read it from this file rather than each
 * keeping their own copy of the same four words.
 *
 * DESCRIPTIVE, NOT BEHAVIOURAL. Nothing in execution reads a role — see the
 * note on `LegRole` in the schema. Exit ordering protects the right leg by
 * sorting BUYs first, off the SIDE, which cannot be mistyped.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** The vocabulary, in the order it is shown. */
export const LEG_ROLES = ['MAIN', 'HEDGE', 'ADJUSTMENT'];
/** Is this a role we recognise? The guard for every external boundary. */
export const isLegRole = (v) => typeof v === 'string' && LEG_ROLES.includes(v);
/**
 * Read a role off a database column or a request body.
 *
 * Returns null for anything unrecognised rather than throwing: a Json or enum
 * column is whatever was last written to it, and a value this build does not
 * know about is "not stated" rather than a reason to fail the whole read.
 */
export const parseLegRole = (v) => (isLegRole(v) ? v : null);
/** How each role reads on screen, and what it means. */
export const LEG_ROLE_LABEL = {
    MAIN: 'Main',
    HEDGE: 'Hedge',
    ADJUSTMENT: 'Adjustment',
};
/**
 * The dense form, for a position row.
 *
 * `ADJUSTMENT` at full length is wider than the strike it sits beside, and
 * the LEG column already carries a side pill, the contract and a status. The
 * full word stays on the tooltip and in the journal, where there is room.
 */
export const LEG_ROLE_SHORT = {
    MAIN: 'MAIN',
    HEDGE: 'HEDGE',
    ADJUSTMENT: 'ADJ',
};
export const LEG_ROLE_MEANING = {
    MAIN: 'The thesis — the leg this position exists to express.',
    HEDGE: 'Bought protection that caps what the main legs can lose.',
    ADJUSTMENT: 'Added after entry to defend or repair the position.',
};
export function splitByRole(legs) {
    const byRole = Object.fromEntries(LEG_ROLES.map((r) => [r, { total: 0, legs: 0 }]));
    const untagged = { total: 0, legs: 0 };
    for (const leg of legs) {
        // A non-finite value is dropped rather than poisoning the sum with NaN.
        const value = Number.isFinite(leg.value) ? leg.value : 0;
        /*
         * Anything that is not a role we know is UNTAGGED — undefined, null, and
         * a word written by a build that had a fourth category. Looking the
         * bucket up by an unvalidated key is what threw.
         */
        const bucket = isLegRole(leg.role) ? byRole[leg.role] : untagged;
        bucket.total += value;
        bucket.legs += 1;
    }
    for (const r of LEG_ROLES) {
        byRole[r].total = Math.round(byRole[r].total * 100) / 100;
    }
    untagged.total = Math.round(untagged.total * 100) / 100;
    return { byRole, untagged, empty: LEG_ROLES.every((r) => byRole[r].legs === 0) };
}
//# sourceMappingURL=legRole.js.map